import { EntityState, EntityAdapter, createEntityAdapter, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { Alert } from '../../domain/models';
import * as fromNotfAlertActions from './alert.actions';
import { LocalizedText } from '@common/models';

export const NotfAlertFeatureKey = 'alert';

export interface INotfAlertState extends EntityState<Alert>
{
    alerts: Alert[];
    qsWord: string;
}

export const adapter: EntityAdapter<Alert> = createEntityAdapter<Alert>({
    selectId: (alert: Alert) => alert.id
});

/**
 * Une action NgRx etale son payload : l'objet recu a perdu le prototype d'« Alert », donc les accesseurs
 * « since » / « until » / « textFr » / « textEn » dont dependent bandeau, tri et compteurs. Toute alerte
 * confiee au store repasse donc par la classe, debarrassee au passage du « type » discriminant de l'action.
 */
const alertFromAction = (source: Alert): Alert =>
{
    const alert: Alert & { type?: string } = Object.assign(Object.create(Alert.prototype), source);

    delete alert.type;

    return alert;
};

/**
 * Les alertes vivent en double dans cet etat : la collection tenue par l'adapter, qui alimente le bandeau
 * (« selectAllAlert » / « getFirstAlert »), et le tableau « alerts », qui alimente la liste et les compteurs
 * d'administration. Chaque ecriture doit tenir les deux a jour et creer de nouvelles references : « select »
 * applique « distinctUntilChanged » et les selecteurs sont memoises, aussi une modification en place laisse
 * l'ecran fige jusqu'au prochain chargement.
 *
 * L'adapter ne sert qu'a lire la collection : ses « updateOne » / « upsertOne » reconstruisent l'entite par
 * spread, ce qui efface le prototype d'« Alert » et ses accesseurs. Les ecritures passent donc par ces helpers,
 * qui remplacent l'entite par reference et laissent « ids » en place.
 */
const putAlert = (state: INotfAlertState, alert: Alert): INotfAlertState =>
{
    const ids: string[] = state.ids as string[];

    return {
        ...state,
        ids: ids.includes(alert.id) ? ids : [...ids, alert.id],
        entities: { ...state.entities, [alert.id]: alert }
    };
};

const dropAlert = (state: INotfAlertState, alertId: string): INotfAlertState =>
{
    const entities: typeof state.entities = { ...state.entities };

    delete entities[alertId];

    return {
        ...state,
        ids: (state.ids as string[]).filter((id: string): boolean => id !== alertId),
        entities
    };
};

const writeAlert = (state: INotfAlertState, alert: Alert, append: boolean = false): INotfAlertState =>
({
    ...putAlert(state, alert),
    alerts: state.alerts.some((a: Alert): boolean => a.id === alert.id)
        ? state.alerts.map((a: Alert): Alert => a.id === alert.id ? alert : a)
        : (append ? [...state.alerts, alert] : state.alerts)
});

/** Bascule l'activation d'une alerte connue dans les deux representations, sans jamais muter l'original. */
const writeActive = (state: INotfAlertState, alertId: string, active: boolean): INotfAlertState =>
{
    const found: Alert = state.alerts.find((a: Alert): boolean => a.id === alertId) ?? state.entities[alertId];

    return found
        ? writeAlert(state, Object.assign(Object.create(Alert.prototype), found, { active }))
        : state;
};

export const initialState: INotfAlertState = adapter.getInitialState({
    alerts: [],
    qsWord: ''
});

export const NotfAlertReducer = createReducer(
    initialState,
    on(
        fromNotfAlertActions.NotfAlertLoadSucceeded,
        (state: INotfAlertState, { alerts }: { alerts: Alert[] }) =>
            adapter.addMany(alerts ?? [], adapter.removeAll(state))
    ),
    on(
        fromNotfAlertActions.NotfAlertsSucceeded,
        (state, { alerts }) =>
            ({
                ...adapter.setAll(alerts ?? [], state),
                alerts: alerts ?? []
            })
    ),
    on(
        fromNotfAlertActions.NotfAlertsQsWordSet,
        (state, { qsWord }: { qsWord: string; }) =>
            ({
                ...state,
                qsWord: qsWord.toLowerCase()
            })
    ),
    on(
        fromNotfAlertActions.NotfAlertsDisableSucceeded,
        (state: INotfAlertState, { alertId }: { alertId: string }): INotfAlertState =>
            writeActive(state, alertId, false)
    ),
    on(
        fromNotfAlertActions.NotfAlertsActivateSucceeded,
        (state: INotfAlertState, { alertId }: { alertId: string }): INotfAlertState =>
            writeActive(state, alertId, true)
    ),
    on(
        fromNotfAlertActions.NotfAlertsDeleteSucceeded,
        (state: INotfAlertState, { alertId }: { alertId: string }): INotfAlertState =>
            ({
                ...dropAlert(state, alertId),
                alerts: state.alerts.filter((a: Alert): boolean => a.id !== alertId)
            })
    ),
    on(
        fromNotfAlertActions.NotfCreateAlertSucceeded,
        (state: INotfAlertState, alert: Alert): INotfAlertState =>
            writeAlert(state, alertFromAction(alert), true)
    ),
    on(
        fromNotfAlertActions.NotfUpdateAlertSucceeded,
        (state: INotfAlertState, alert: Alert): INotfAlertState =>
            writeAlert(state, alertFromAction(alert))
    )
);
